// download.ts — Trigger a browser file download from in-memory data.
//
// Single source of truth for the "blob → object URL → anchor → click →
// revoke" dance that used to live in 4 separate copies (device-pairing,
// settings-panel, export-import, platform.js) with drift in revoke timing
// and error handling.
//
// Usage:
//   downloadBlob(blobOrBytesOrString, 'file.json', { mimeType: 'application/json' })
//
// `data` may be a Blob, a TypedArray / ArrayBuffer, or a string. Non-Blob
// inputs are wrapped using `mimeType` (defaults to 'application/octet-stream').
//
// Returns true on success, false on failure. Failures are swallowed to match
// the most-defensive caller (platform.js _downloadViaAnchor) — UI-surface
// callers that need to know about failure should check the return value.

const REVOKE_DELAY_MS = 1000;

/**
 * Trigger a browser file download from in-memory data.
 *
 * @param data Source bytes. A Blob is used as-is; a BufferSource (ArrayBuffer /
 *   typed array / DataView) or string is wrapped in a Blob tagged with
 *   `opts.mimeType`. (`BufferSource` is exactly what the `Blob` constructor
 *   accepts, so this excludes `SharedArrayBuffer`, which `new Blob([...])`
 *   rejects.)
 * @param filename Suggested download filename (the anchor's `download` attr).
 * @param opts Optional Blob options; `mimeType` defaults to
 *   'application/octet-stream' for non-Blob input.
 * @returns true on success; false if the environment lacks URL/document or the
 *   download dance threw.
 */
export function downloadBlob(
  data: Blob | BufferSource | string,
  filename: string,
  opts: { mimeType?: string } = {}
): boolean {
  try {
    if (typeof URL === 'undefined' || typeof document === 'undefined') return false;
    const blob =
      data instanceof Blob
        ? data
        : new Blob([data], { type: opts.mimeType || 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Defer revoke so the browser has time to start the download. Immediate
    // revoke races the download-initiation in Safari and Capacitor WKWebView.
    setTimeout(() => {
      try {
        URL.revokeObjectURL(url);
      } catch (_) {}
    }, REVOKE_DELAY_MS);
    return true;
  } catch (_) {
    return false;
  }
}
